import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Snackbar,
  Alert as MuiAlert,
} from '@mui/material';
import { Refresh as RefreshIcon, Sms as SmsIcon } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomerSmsLogs, useResendCustomerSms } from '../hooks/useCustomers';

const TRIGGER_LABELS: Record<string, string> = {
  auto: 'Auto',
  manual: 'Manual',
  command: 'Command',
};

const today = new Date().toISOString().split('T')[0];
const sevenDaysAgo = new Date(Date.now() - 7 * 86400_000).toISOString().split('T')[0];

const CustomerSmsLogs: React.FC = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    date_from: sevenDaysAgo,
    date_to: today,
    status: '',
    trigger: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [resendTarget, setResendTarget] = useState<{
    id: number;
    name: string;
    date: string;
  } | null>(null);

  const { data, isLoading, error } = useCustomerSmsLogs({
    date_from: filters.date_from || undefined,
    date_to: filters.date_to || undefined,
    status: filters.status || undefined,
    trigger: filters.trigger || undefined,
  });

  const resendMutation = useResendCustomerSms();

  const logs: any[] = data?.logs?.data ?? [];
  const summary = data?.summary;

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['customers', 'sms-logs'] });
  };

  const handleResend = async (log: any) => {
    try {
      await resendMutation.mutateAsync({ id: log.customer_id, date: log.sale_date });
      setSnackbar({
        open: true,
        message: `SMS resent to ${log.customer_name}`,
        severity: 'success',
      });
      handleRefresh();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message ?? 'Failed to send SMS',
        severity: 'error',
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            SMS Receipt Log
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
            Every SMS receipt attempt — sent, failed, manual resends
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} disabled={isLoading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* summary cards */}
      {summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Total attempts', value: summary.total, color: '#ff9800' },
            { label: 'Sent', value: summary.sent, color: '#4caf50' },
            { label: 'Failed', value: summary.failed, color: '#f44336' },
          ].map((s) => (
            <Paper
              key={s.label}
              sx={{
                p: 2,
                textAlign: 'center',
                border: `1px solid ${s.color}33`,
                flex: 1,
                minWidth: 120,
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ color: s.color }}>
                {s.value}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                {s.label}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* filters */}
      <Paper
        sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <TextField
          label="From"
          type="date"
          size="small"
          value={filters.date_from}
          onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={filters.date_to}
          onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="Status"
          select
          size="small"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="sent">Sent</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
        </TextField>
        <TextField
          label="Trigger"
          select
          size="small"
          value={filters.trigger}
          onChange={(e) => setFilters((f) => ({ ...f, trigger: e.target.value }))}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="auto">Auto</MenuItem>
          <MenuItem value="manual">Manual</MenuItem>
          <MenuItem value="command">Command</MenuItem>
        </TextField>
      </Paper>

      {/* loading / error */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="primary" />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load SMS logs. Please refresh.
        </Alert>
      )}

      {/* table */}
      {!isLoading && !error && (
        <Paper sx={{ overflow: 'hidden' }}>
          {logs.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>
                No SMS attempts found for the selected filters.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(255,152,0,0.08)' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sale Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Trigger</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sent By</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                  <TableCell sx={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log: any) => (
                  <TableRow
                    key={log.id}
                    sx={{
                      '& td': { borderBottom: '1px solid rgba(255,152,0,0.08)' },
                      bgcolor: log.status === 'failed' ? 'rgba(244,67,54,0.04)' : 'transparent',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {log.customer_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(log.sale_date).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}
                      >
                        {log.phone_number}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      {log.message ? (
                        <Tooltip
                          title={
                            <Typography
                              variant="caption"
                              sx={{ whiteSpace: 'pre-wrap', display: 'block', maxWidth: 320 }}
                            >
                              {log.message}
                            </Typography>
                          }
                          arrow
                          placement="top"
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255,255,255,0.6)',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              cursor: 'help',
                            }}
                          >
                            {log.message}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={log.status === 'sent' ? 'Sent' : 'Failed'}
                        color={log.status === 'sent' ? 'success' : 'error'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        {TRIGGER_LABELS[log.trigger] ?? log.trigger}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        {log.sent_by_name ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                        {new Date(log.created_at).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {log.status === 'failed' && (
                        <Tooltip title="Resend SMS">
                          <span>
                            <IconButton
                              size="small"
                              disabled={resendMutation.isPending}
                              onClick={() => handleResend(log)}
                              sx={{
                                color: 'rgba(33,150,243,0.7)',
                                '&:hover': { color: '#2196f3', bgcolor: 'rgba(33,150,243,0.1)' },
                              }}
                            >
                              <SmsIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <MuiAlert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default CustomerSmsLogs;
